import { makeApiRequest } from './api';
import { getCookie } from './storage';
import type { ApiResponse } from './admin-auth';

export enum ETenantStaffRole {
  TENANT_OWNER = 'tenant_owner',
  TENANT_STAFF_ADMIN = 'tenant_staff_admin',
  TENANT_STAFF_MEMBER = 'tenant_staff_member',
}

export type TenantUserStatus = 'active' | 'invited' | 'suspended';

export interface ITenantStaff {
  id: string;
  tenantId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: ETenantStaffRole;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TenantStaffAuthResponse {
  staff: ITenantStaff;
  token: string;
}

export interface TenantStaffDashboardMetrics {
  totalUsers: number;
  totalCampaigns: number;
  activeCampaigns: number;
  totalStaff: number;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface TenantUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  status: TenantUserStatus;
  roles: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface TenantCampaign {
  id: string;
  name: string;
  brandName?: string;
  status: string;
  objective: string;
  budgetType: string;
  totalBudgetCap: number;
  dailyBudgetCap: number;
  startDate: string;
  endDate: string;
  createdAt?: string;
}

const tenantIdFromCookie = (): string | undefined =>
  getCookie('tenantId') || undefined;

export const tenantStaffLogin = (email: string, password: string) =>
  makeApiRequest<ApiResponse<TenantStaffAuthResponse>>(
    '/auth/tenant-staff-login',
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        tenantId: tenantIdFromCookie(),
      }),
    }
  );

export const loadTenantStaffProfile = (token: string) =>
  makeApiRequest<ApiResponse<{ profile: ITenantStaff }>>(
    '/tenant-staff/profile',
    { token }
  );

export const getTenantStaffDashboard = (token: string) =>
  makeApiRequest<ApiResponse<{ metrics: TenantStaffDashboardMetrics }>>(
    '/tenant-staff/overview/dashboard',
    { token }
  );

export const getTenantStaffCampaigns = (
  token: string,
  params: { page?: number; limit?: number } = {}
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return makeApiRequest<ApiResponse<PaginatedResult<TenantCampaign>>>(
    `/tenant-staff/campaigns${qs ? `?${qs}` : ''}`,
    { token }
  );
};

export const getTenantStaffUsers = (
  token: string,
  params: { page?: number; limit?: number; search?: string } = {}
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  const qs = query.toString();
  return makeApiRequest<ApiResponse<PaginatedResult<TenantUser>>>(
    `/tenant-staff/users${qs ? `?${qs}` : ''}`,
    { token }
  );
};

export const inviteTenantUser = (
  token: string,
  payload: { email: string; firstName?: string; lastName?: string }
) =>
  makeApiRequest<ApiResponse<{ user: TenantUser }>>(
    '/tenant-staff/users/invite',
    {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    }
  );

export const updateTenantUserStatus = (
  token: string,
  userId: string,
  status: TenantUserStatus
) =>
  makeApiRequest<ApiResponse<{ user: TenantUser }>>(
    `/tenant-staff/users/${userId}/status`,
    {
      method: 'PUT',
      token,
      body: JSON.stringify({ status }),
    }
  );

export const getTenantStaffList = (token: string) =>
  makeApiRequest<ApiResponse<{ items: ITenantStaff[] }>>(
    '/tenant-staff/staff',
    {
      token,
    }
  );

export const createTenantStaff = (
  token: string,
  payload: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    role?: ETenantStaffRole;
  }
) =>
  makeApiRequest<ApiResponse<{ staff: ITenantStaff }>>('/tenant-staff/staff', {
    method: 'POST',
    token,
    body: JSON.stringify(payload),
  });

export const updateTenantStaff = (
  token: string,
  id: string,
  payload: {
    firstName?: string;
    lastName?: string;
    role?: ETenantStaffRole;
    isActive?: boolean;
  }
) =>
  makeApiRequest<ApiResponse<{ staff: ITenantStaff }>>(
    `/tenant-staff/staff/${id}`,
    {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    }
  );

export const removeTenantStaff = (token: string, id: string) =>
  makeApiRequest<ApiResponse<unknown>>(`/tenant-staff/staff/${id}`, {
    method: 'DELETE',
    token,
  });

// ─── Advertiser billing ──────────────────────────────────────────────────────
//
// The controls that replaced the campaign approval queue: assign each
// advertiser a payment method, and fund a postpaid one out of the tenant's own
// wallet. The tenant wallet is itself funded by a platform super admin.

export type PaymentMethod = 'prepaid' | 'postpaid';

export interface TenantWalletSummary {
  tenantId: string;
  paymentMethod: PaymentMethod;
  currency: string;
  /** Total ever funded by a super admin; null when never funded. */
  funded: number | null;
  /** Total already handed on to advertiser wallets. */
  disbursed: number;
  /** What is left to give out; null when never funded. */
  available: number | null;
  canFundAdvertisers: boolean;
  lastFundedAmount: number | null;
  lastFundedAt: string | null;
}

export interface TenantWalletTransaction {
  id: string;
  type: 'grant' | 'adjust' | 'reserve' | 'release' | 'disburse' | 'reclaim';
  amount: number;
  balanceAfter: number | null;
  reason?: string | null;
  createdBy?: string | null;
  createdAt: string;
}

export interface AdvertiserListRow {
  businessId: string;
  businessName: string;
  email: string | null;
  paymentMethod: PaymentMethod;
  currency: string;
  walletId: string | null;
  walletStatus: string | null;
  balance: number;
  reservedAmount: number;
  availableBalance: number;
  lifetimeSpend: number;
}

export interface AdvertiserBilling {
  tenantId: string;
  businessId: string;
  businessName: string | null;
  paymentMethod: PaymentMethod;
  /** False while the advertiser is still on the value migrated from the tenant. */
  profileExists: boolean;
  currency: string;
  balance: number;
  reservedAmount: number;
  availableBalance: number;
  lifetimeTopUp: number;
  lifetimeSpend: number;
  walletId: string | null;
  walletStatus: string | null;
}

export interface AdvertiserBillingEvent {
  id: string;
  previousPaymentMethod: PaymentMethod | null;
  newPaymentMethod: PaymentMethod;
  actorId: string | null;
  actorType: 'admin' | 'tenant_staff' | 'system';
  reason: string | null;
  createdAt: string;
}

export interface AdvertiserFundingResult {
  walletId: string;
  businessId: string;
  currency: string;
  amount: number;
  availableBalance: number;
  tenantAvailableBalance: number;
  transactionId: string;
}

export const getTenantWallet = (token: string) =>
  makeApiRequest<ApiResponse<{ wallet: TenantWalletSummary }>>(
    '/tenant-staff/advertisers/wallet',
    { token }
  );

export const getTenantWalletTransactions = (
  token: string,
  params: { page?: number; limit?: number } = {}
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return makeApiRequest<ApiResponse<PaginatedResult<TenantWalletTransaction>>>(
    `/tenant-staff/advertisers/wallet/transactions${qs ? `?${qs}` : ''}`,
    { token }
  );
};

export const getTenantAdvertisers = (
  token: string,
  params: {
    page?: number;
    limit?: number;
    search?: string;
    paymentMethod?: PaymentMethod;
  } = {}
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.paymentMethod) query.set('paymentMethod', params.paymentMethod);
  const qs = query.toString();
  return makeApiRequest<ApiResponse<PaginatedResult<AdvertiserListRow>>>(
    `/tenant-staff/advertisers${qs ? `?${qs}` : ''}`,
    { token }
  );
};

export const getAdvertiserBilling = (token: string, businessId: string) =>
  makeApiRequest<ApiResponse<{ billing: AdvertiserBilling }>>(
    `/tenant-staff/advertisers/${businessId}/billing`,
    { token }
  );

export const getAdvertiserBillingHistory = (
  token: string,
  businessId: string,
  params: { page?: number; limit?: number } = {}
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return makeApiRequest<ApiResponse<PaginatedResult<AdvertiserBillingEvent>>>(
    `/tenant-staff/advertisers/${businessId}/billing/history${qs ? `?${qs}` : ''}`,
    { token }
  );
};

export const setAdvertiserPaymentMethod = (
  token: string,
  businessId: string,
  payload: { paymentMethod: PaymentMethod; reason: string }
) =>
  makeApiRequest<ApiResponse<{ billing: AdvertiserBilling }>>(
    `/tenant-staff/advertisers/${businessId}/billing/payment-method`,
    { method: 'PUT', token, body: JSON.stringify(payload) }
  );

/**
 * `requestId` identifies one form submission: a double-click or retry of the
 * same submission is applied once (the repeat is answered 409).
 */
export const fundAdvertiserWallet = (
  token: string,
  businessId: string,
  payload: {
    amount: number;
    reason: string;
    reference?: string;
    requestId?: string;
  }
) =>
  makeApiRequest<ApiResponse<{ funding: AdvertiserFundingResult }>>(
    `/tenant-staff/advertisers/${businessId}/wallet/fund`,
    { method: 'POST', token, body: JSON.stringify(payload) }
  );

/** `requestId`: one per form submission, as for {@link fundAdvertiserWallet}. */
export const reverseAdvertiserFunding = (
  token: string,
  businessId: string,
  payload: {
    amount: number;
    reason: string;
    reference?: string;
    requestId?: string;
  }
) =>
  makeApiRequest<ApiResponse<{ reversal: AdvertiserFundingResult }>>(
    `/tenant-staff/advertisers/${businessId}/wallet/reverse`,
    { method: 'POST', token, body: JSON.stringify(payload) }
  );

// ─── Business management ─────────────────────────────────────────────────────
//
// A *business* is the advertiser account: it owns campaigns and a wallet. A
// *user* is a person with a login. They meet through business membership,
// which is why a business carries both an owner and a team.

export interface BusinessOwner {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  status?: string | null;
}

export interface TenantBusinessDetail {
  id: string;
  name: string;
  email?: string | null;
  type?: string | null;
  industryType?: string | null;
  website?: string | null;
  phoneNumber?: string | null;
  city?: string | null;
  country?: string | null;
  createdAt?: string | null;
  owner: BusinessOwner | null;
  billing: {
    paymentMethod: PaymentMethod;
    profileExists: boolean;
    currency: string;
    balance: number;
    reservedAmount: number;
    availableBalance: number;
    lifetimeSpend: number;
    walletStatus: string | null;
  };
  metrics: {
    totalCampaigns: number;
    activeCampaigns: number;
    teamSize: number;
  };
}

export interface TenantBusinessMember {
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role: string;
  status?: string | null;
  isOwner: boolean;
  joinedAt?: string | null;
}

export interface TenantBusinessCampaignRow {
  id: string;
  name: string;
  businessId: string;
  status: string;
  objective?: string;
  totalBudgetCap: number;
  dailyBudgetCap: number;
  startDate?: string | null;
  endDate?: string | null;
  spend: number;
  reach: number;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  revenue: number;
  roas: number;
  cpa: number;
}

export interface TenantBusinessCampaigns {
  dateRange: string;
  periodLabel: string;
  startDate: string;
  endDate: string;
  lastSyncedAt?: string | null;
  summary: {
    totalSpend: number;
    totalImpressions: number;
    totalClicks: number;
    ctr: number;
    totalConversions: number;
    conversionRate: number;
    totalRevenue: number;
    roas: number;
    cpm: number;
    cpc: number;
    cpa: number;
  };
  campaigns: TenantBusinessCampaignRow[];
}

export const getTenantBusinesses = (
  token: string,
  params: {
    page?: number;
    limit?: number;
    search?: string;
    paymentMethod?: PaymentMethod;
  } = {}
) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.paymentMethod) query.set('paymentMethod', params.paymentMethod);
  const qs = query.toString();
  return makeApiRequest<ApiResponse<PaginatedResult<AdvertiserListRow>>>(
    `/tenant-staff/businesses${qs ? `?${qs}` : ''}`,
    { token }
  );
};

export const getTenantBusinessDetail = (token: string, businessId: string) =>
  makeApiRequest<ApiResponse<{ business: TenantBusinessDetail }>>(
    `/tenant-staff/businesses/${businessId}`,
    { token }
  );

export const getTenantBusinessMembers = (token: string, businessId: string) =>
  makeApiRequest<ApiResponse<{ members: TenantBusinessMember[] }>>(
    `/tenant-staff/businesses/${businessId}/users`,
    { token }
  );

export const getTenantBusinessCampaigns = (
  token: string,
  businessId: string,
  dateRange?: string
) =>
  makeApiRequest<ApiResponse<TenantBusinessCampaigns>>(
    `/tenant-staff/businesses/${businessId}/campaigns${
      dateRange ? `?dateRange=${dateRange}` : ''
    }`,
    { token }
  );

export const getTenantBusinessCampaignAnalytics = (
  token: string,
  businessId: string,
  campaignId: string,
  dateRange?: string
) =>
  makeApiRequest<ApiResponse<{ detail: Record<string, unknown> }>>(
    `/tenant-staff/businesses/${businessId}/campaigns/${campaignId}/analytics${
      dateRange ? `?dateRange=${dateRange}` : ''
    }`,
    { token }
  );

// --- Consolidated report -----------------------------------------------

export interface TenantStaffReportBusiness {
  id: string;
  name: string;
}

export interface ConsolidatedReportSections {
  spend?: boolean;
  deliveryMetrics?: boolean;
  walletTransactions?: boolean;
}

export interface ConsolidatedReportRequest {
  businessIds: string[];
  from: string;
  to: string;
  sections: ConsolidatedReportSections;
}

export interface ConsolidatedReportBusinessRow {
  businessId: string;
  businessName: string;
  spend?: number;
  metrics?: {
    impressions: number;
    clicks: number;
    conversions: number;
    revenue: number;
    ctr: number;
  };
}

export interface ConsolidatedReportCampaignRow {
  campaignId: string;
  campaignName: string;
  businessId: string;
  businessName: string;
  dailyBudgetCap: number;
  totalBudgetCap: number;
  spend?: number;
  metrics?: {
    impressions: number;
    clicks: number;
    conversions: number;
    revenue: number;
    ctr: number;
  };
}

export interface ConsolidatedReportTransactionRow {
  id: string;
  businessId: string;
  businessName: string;
  createdAt: string;
  type: string;
  amount: number;
  balanceAfter: number;
  currency: string;
  description?: string;
  referenceId?: string;
}

export interface ConsolidatedReportTotals {
  spend?: number;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  revenue?: number;
  transactionsTotal?: number;
}

export interface ConsolidatedReportResult {
  from: string;
  to: string;
  businesses: ConsolidatedReportBusinessRow[];
  campaigns?: ConsolidatedReportCampaignRow[];
  transactions?: ConsolidatedReportTransactionRow[];
  totals: ConsolidatedReportTotals;
}

export const getTenantStaffReportBusinesses = (token: string) =>
  makeApiRequest<ApiResponse<{ businesses: TenantStaffReportBusiness[] }>>(
    '/tenant-staff/reports/businesses',
    { token }
  );

export const previewConsolidatedReport = (
  token: string,
  payload: ConsolidatedReportRequest
) =>
  makeApiRequest<ApiResponse<{ report: ConsolidatedReportResult }>>(
    '/tenant-staff/reports/consolidated/preview',
    { method: 'POST', token, body: JSON.stringify(payload) }
  );

/** Backend returns `text/csv`, not JSON — `makeApiRequest` falls back to `.blob()` for non-JSON responses. */
export const exportConsolidatedReport = (
  token: string,
  payload: ConsolidatedReportRequest
) =>
  makeApiRequest<Blob>('/tenant-staff/reports/consolidated/export', {
    method: 'POST',
    token,
    body: JSON.stringify(payload),
  });
